import {
  IEventResponseModel,
  ISingleEventResponseModel,
  IProductTypeResponseModel,
  IEventApiResponseModel,
} from "../models/library.models";

// Endpoint URLs
const eventBaseUrl = "https://maitlandapi.wpengine.com/wp-json/wp/v2/event";
const productTypeBaseUrl =
  "https://maitlandapi.wpengine.com/wp-json/wp/v2/product_type";

// Hyperlink Base URL for linking to events
const eventBaseHyperlink =
  "https://www.maitlandlibrary.com.au/whats-on/upcoming-events/";

// Internals
let libraryProductTypes: IProductTypeResponseModel[] | null = null;

export function getLibraryProductTypes(): Promise<IProductTypeResponseModel[]> {
  const request: RequestInfo = new Request(
    `${productTypeBaseUrl}?parent=1021&per_page=100&page=1`
  );

  return fetch(request)
    .then((response) => response.json())
    .then((response) => response as IProductTypeResponseModel[]);
}

export interface GetLibraryEventsProps {
  productType?: string;
}

export async function getCurrentLibraryEvents(
  props: GetLibraryEventsProps = null
): Promise<IEventResponseModel[]> {
  // ensure we have the product types loaded
  if (libraryProductTypes === null) {
    libraryProductTypes = await getLibraryProductTypes();
  }

  // handle filtering
  let productTypeParam = "";
  if (props?.productType) {
    productTypeParam = `&product_type=${props.productType}`;
  }

  let pageSizeParam = "&per_page=100";
  let pageParam = "&page=1";

  // handle request
  const request: RequestInfo = new Request(
    `${eventBaseUrl}?share_entity=1012${pageSizeParam}${pageParam}${productTypeParam}`,
    {
      method: "GET",
    }
  );

  const now = new Date();

  // handle the buffoonery of the WP Engine API data
  return fetch(request)
    .then((response) => response.json())
    .then((response: IEventApiResponseModel[]) => {
      let results: IEventResponseModel[] = [];
      results = response.flatMap((item) => {
        // Split up all the extra date details
        var dateTime = item.ACF.start_date;
        var dateSplit = dateTime.split(" ");
        var finalDate = new Date(dateSplit[0]);

        // Work out if there are repeater dates and replace final date with them
        var dateNow = new Date();
        if (dateNow > finalDate) {
          var repeatDates = item.ACF.system_repeater_dates;

          // Loop through repeat dates if any
          if (repeatDates) {
            for (let repeatDate in repeatDates) {
              // Get the repeat dates and split it into the date
              var thisRepeatDate = item.ACF.system_repeater_dates[repeatDate];
              var repeatDateSplit = thisRepeatDate.date.split(" ");
              var finalRepeatDate = new Date(repeatDateSplit[0]);

              if (finalRepeatDate >= dateNow) {
                // Set final date to the repeast date and break out of the loop.
                finalDate = finalRepeatDate;
                break;
              }
            }
          }
        }

        // Remove the unecessary HTML in description
        var description = item.excerpt.rendered;
        var trimmedDescription = description
          .replaceAll("<p>", "")
          .replaceAll("</p>", "")
          .replaceAll("\n", "<br />");

        // Add result into a model
        var result: IEventResponseModel = {
          id: item.id,
          title: item.title.rendered,
          image: item.fimg_url,
          description: trimmedDescription,
          address: item.ACF.location.address,
          city: item.ACF.location.city,
          date: finalDate,
          startTime: item.ACF.start_time,
          endTime: item.ACF.end_time,
          hyperlink: `${eventBaseHyperlink}${item.slug}`,
          bookingLink: item.ACF.ticket_booking_url,
          productTypeId: item.product_type,
          productTypeName: [],
          repeatType: item.ACF.repeat_type,
        };

        // If this event concluded has concluded then it should be ignored
        if (result.date < now) {
          return [];
        }

        // Limit product types shown to only those in the Library list
        item.product_type.forEach((productTypeId) => {
          var productType = libraryProductTypes.find(
            (x) => x.id === productTypeId
          );
          if (productType) {
            result.productTypeName.push(productType.name);
          }
        });

        return result;
      });

      return results;
    });
}

// Call event endpoint
export function getSingleEvent(eventId): Promise<ISingleEventResponseModel> {
  // Build endpoint request
  const request: RequestInfo = new Request(`${eventBaseUrl}/${eventId}`, {
    method: "GET",
  });

  // return the request
  return fetch(request)
    .then((response) => response.json())
    .then((response) => {
      // Split up all the extra date details
      var dateTime = response.ACF.start_date;
      var dateSplit = dateTime.split(" ");
      var finalDate = new Date(dateSplit[0]);

      // Work out if there are repeater dates and replace final date with them
      var dateNow = new Date();
      if (dateNow > finalDate) {
        var repeatDates = response.ACF.system_repeater_dates;

        // Loop through repeat dates if any
        for (let repeatDate in repeatDates) {
          // Get the repeat dates and split it into the date
          var thisRepeatDate = response.ACF.system_repeater_dates[repeatDate];
          var repeatDateSplit = thisRepeatDate.date.split(" ");
          var finalRepeatDate = new Date(repeatDateSplit[0]);

          if (finalRepeatDate >= dateNow) {
            // Set final date to the repeast date and break out of the loop.
            finalDate = finalRepeatDate;
            break;
          }
        }
      }

      // Remove the unecessary HTML in description
      var description = response.content.rendered;
      var trimmedDescription = description
        .replaceAll("\n", "")
        .replaceAll("<p>", '<p style="padding-bottom:10px">');

      // Remove the unecessary HTML in description
      var excerpt = response.excerpt.rendered;
      var trimmedExcerpt = excerpt
        .replaceAll("<p>", "")
        .replaceAll("</p>", "")
        .replaceAll("\n", "<br />");

      // Add result into a model
      var results: ISingleEventResponseModel = {
        id: response.id,
        title: response.title.rendered,
        image: response.fimg_url,
        excerpt: trimmedExcerpt,
        description: trimmedDescription,
        address_name: response.ACF.location.name,
        street_name: response.ACF.location.street_name,
        street_number: response.ACF.location.street_number,
        address: response.ACF.location.address,
        city: response.ACF.location.city,
        date: finalDate,
        contact_phone: response.ACF.contact_details.phone,
        contact_email: response.ACF.contact_details.email,
        startTime: response.ACF.start_time,
        endTime: response.ACF.end_time,
        hyperlink: `${eventBaseHyperlink}${response.slug}`,
        bookingLink: response.ACF.ticket_booking_url,
        repeatDates: response.ACF.include_dates,
        systemRepeatDates: response.ACF.system_repeater_dates,
        images: response.ACF.images,
        free_entry: response.ACF.features.free_entry,
        price_from: response.ACF.price_from,
      };

      // return results
      return results as ISingleEventResponseModel;
    });
}

// Call event endpoint
// export function getProductType(productTypeId): Promise<string> {
//   // Build endpoint request
//   const request: RequestInfo = new Request(
//     `${productTypeBaseUrl}/${productTypeId}`,
//     {
//       method: "GET",
//     }
//   );

//   // return the request
//   return fetch(request)
//     .then((response) => response.json())
//     .then((response) => {
//       var result = response.name;

//       // return results
//       return result;
//     });
// }
